import type { Sheet } from "../domain/cutPlan";

interface Props {
  sheets: Sheet[];
}

const COLORS = [
  "#ffadad",
  "#ffd6a5",
  "#fdffb6",
  "#caffbf",
  "#9bf6ff",
  "#a0c4ff",
  "#bdb2ff",
  "#ffc6ff",
  "#fffffc",
  "#e0bbe4",
];

/**
 * SVG render of every sheet with parts laid out on it. Designed to print
 * cleanly on letter paper — gives the CNC operator a one-page-per-sheet plan.
 */
export function CutSheet({ sheets }: Props): JSX.Element {
  if (sheets.length === 0) return <p>No sheets generated.</p>;
  return (
    <div className="cut-sheets">
      {sheets.map((sheet) => {
        // pick a px scale so the sheet's long edge is ~640px
        const scale = 640 / sheet.length;
        const W = sheet.length * scale;
        const H = sheet.width * scale;
        return (
          <div key={sheet.index} className="cut-sheet">
            <h3>
              Sheet {sheet.index} — {sheet.thickness} cm stock — {sheet.length}{" "}
              × {sheet.width} cm
            </h3>
            <svg
              width={W + 2}
              height={H + 2}
              style={{ background: "#fafafa", border: "1px solid #333" }}
            >
              <rect
                x={1}
                y={1}
                width={W}
                height={H}
                fill="#fff"
                stroke="#333"
              />
              {sheet.placements.map((p, i) => {
                const x = p.x * scale + 1;
                const y = p.y * scale + 1;
                const w = p.drawnLength * scale;
                const h = p.drawnWidth * scale;
                const fill = COLORS[i % COLORS.length];
                // diagonal trim line for trapezoidal parts
                const trimLine =
                  p.part.angledTop && p.part.shortEdge !== undefined
                    ? renderTrim(p, scale, x, y, w, h)
                    : null;
                return (
                  <g key={i}>
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      fill={fill}
                      stroke="#000"
                      strokeWidth={0.5}
                    />
                    {trimLine}
                    <text
                      x={x + w / 2}
                      y={y + h / 2 - 4}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#000"
                    >
                      {p.part.label}
                    </text>
                    <text
                      x={x + w / 2}
                      y={y + h / 2 + 8}
                      textAnchor="middle"
                      fontSize={9}
                      fill="#333"
                    >
                      {p.drawnLength.toFixed(1)} × {p.drawnWidth.toFixed(1)} cm
                      {p.rotated ? " (R)" : ""}
                    </text>
                  </g>
                );
              })}
            </svg>
            <ul className="part-notes">
              {sheet.placements
                .filter((p) => p.part.notes)
                .map((p, i) => (
                  <li key={i}>
                    <strong>{p.part.label}:</strong> {p.part.notes}
                  </li>
                ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function renderTrim(
  p: import("../domain/cutPlan").PlacedPart,
  scale: number,
  x: number,
  y: number,
  w: number,
  h: number,
): JSX.Element {
  // The trapezoid's "length" edge is the long edge; short edge is parallel.
  // We draw a diagonal line from (long edge start) to (short edge end).
  const longLen = (p.rotated ? p.part.width : p.part.length) * scale;
  const shortLen =
    ((p.part.shortEdge ?? longLen) / Math.max(p.part.length, 0.0001)) *
    (p.rotated ? p.part.width : p.part.length) *
    scale;
  // Simplification: draw line from top-right corner down to the short-edge offset on top edge.
  // (Operator uses drawn dimensions printed underneath as the source of truth.)
  const offset = longLen - shortLen;
  return (
    <line
      x1={x + w}
      y1={y}
      x2={x + w - offset}
      y2={y + (p.rotated ? 0 : h)}
      stroke="#c00"
      strokeWidth={1}
      strokeDasharray="4 3"
    />
  );
}
